import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import { SITE_COPYRIGHT } from '../lib/siteOperator';
import { PageHeader } from '../components/navigation';
import { ModernCard } from '../components/ui';
import { 
  MoreHorizontal,
  Trophy,
  Calendar,
  Heart,
  MessageSquare,
  UserCircle,
  Settings,
  HelpCircle,
  Bell,
  Shield,
  CreditCard,
  LogOut,
  ChevronRight,
  Star,
  Crown,
  TrendingUp,
  Award,
  Target,
  Users,
  BookOpen,
  CheckSquare,
  Globe
} from 'lucide-react';

const MorePage: React.FC = () => {
  const navigate = useNavigate();

  // SEO optimization
  useSEO({
    title: 'More - MyNewStart',
    description: 'Access additional features, settings, and tools for your American journey.',
    keywords: ['more', 'settings', 'tools', 'features', 'profile'],
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'More', url: '/more' }
    ]
  });

  const mainSections = [
    {
      id: 'milestones',
      title: 'Milestones & Progress',
      description: 'Track your achievements and learning progress',
      icon: Trophy,
      path: '/milestones',
      color: 'blue'
    },
    {
      id: 'tools',
      title: 'Tools & Resources',
      description: 'Calculators, templates, and practical helpers',
      icon: Settings,
      path: '/library',
      color: 'green'
    },
    {
      id: 'upcoming',
      title: 'Upcoming',
      description: 'Events, deadlines, and important reminders',
      icon: Calendar,
      path: '/upcoming',
      color: 'purple'
    },
    {
      id: 'reflections',
      title: 'My Reflections',
      description: 'Personal journaling and goal tracking',
      icon: Heart,
      path: '/reflections',
      color: 'pink'
    },
    {
      id: 'chat',
      title: 'Ask Mini Angel',
      description: 'AI assistant for personalized guidance',
      icon: MessageSquare,
      path: '/chat',
      color: 'indigo'
    }
  ];

  const settingsSections = [
    {
      id: 'profile',
      title: 'Profile & Settings',
      description: 'Manage your account and preferences',
      icon: UserCircle,
      path: '/profile-settings',
      color: 'gray'
    },
    {
      id: 'notifications',
      title: 'Notifications',
      description: 'Manage your notification preferences',
      icon: Bell,
      path: '/profile-settings',
      color: 'yellow'
    },
    {
      id: 'security',
      title: 'Security & Privacy',
      description: 'Account security and privacy settings',
      icon: Shield,
      path: '/profile-settings',
      color: 'red'
    },
    {
      id: 'billing',
      title: 'Billing & Subscription',
      description: 'Manage your subscription and payments',
      icon: CreditCard,
      path: '/subscription',
      color: 'green'
    }
  ];

  const supportSections = [
    {
      id: 'help',
      title: 'Help & Support',
      description: 'Get help and contact support',
      icon: HelpCircle,
      path: '/help',
      color: 'blue'
    }
  ];

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      purple: 'bg-purple-100 text-purple-600',
      pink: 'bg-pink-100 text-pink-600',
      indigo: 'bg-indigo-100 text-indigo-600',
      gray: 'bg-gray-100 text-gray-600',
      yellow: 'bg-yellow-100 text-yellow-600',
      red: 'bg-red-100 text-red-600'
    };
    return colorMap[color as keyof typeof colorMap] || 'bg-gray-100 text-gray-600';
  };

  const handleSignOut = async () => {
    try {
      const { supabase } = await import('../lib/supabaseClient');
      await supabase.auth.signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 py-4">
          <PageHeader
            title="More"
            description="Additional features and settings"
            icon={<MoreHorizontal className="h-6 w-6 text-gray-600" />}
          />
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 space-y-6">
        {/* Main Sections */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Features</h3>
          <div className="space-y-3">
            {mainSections.map((section) => (
              <ModernCard key={section.id} className="p-4">
                <button
                  onClick={() => navigate(section.path)}
                  className="w-full flex items-center justify-between"
                >
                  <div className="flex items-center">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 ${getColorClasses(section.color)}`}>
                      <section.icon className="w-6 h-6" />
                    </div>
                    <div className="text-left">
                      <h4 className="font-semibold text-gray-900 mb-1">{section.title}</h4>
                      <p className="text-sm text-gray-600">{section.description}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              </ModernCard>
            ))}
          </div>
        </div>

        {/* Settings Sections */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Settings</h3>
          <div className="space-y-3">
            {settingsSections.map((section) => (
              <ModernCard key={section.id} className="p-4">
                <button
                  onClick={() => navigate(section.path)}
                  className="w-full flex items-center justify-between"
                >
                  <div className="flex items-center">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 ${getColorClasses(section.color)}`}>
                      <section.icon className="w-6 h-6" />
                    </div>
                    <div className="text-left">
                      <h4 className="font-semibold text-gray-900 mb-1">{section.title}</h4>
                      <p className="text-sm text-gray-600">{section.description}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              </ModernCard>
            ))}
          </div>
        </div>

        {/* Support Sections */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Support</h3>
          <div className="space-y-3">
            {supportSections.map((section) => (
              <ModernCard key={section.id} className="p-4">
                <button
                  onClick={() => navigate(section.path)}
                  className="w-full flex items-center justify-between"
                >
                  <div className="flex items-center">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mr-4 ${getColorClasses(section.color)}`}>
                      <section.icon className="w-6 h-6" />
                    </div>
                    <div className="text-left">
                      <h4 className="font-semibold text-gray-900 mb-1">{section.title}</h4>
                      <p className="text-sm text-gray-600">{section.description}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              </ModernCard>
            ))}
          </div>
        </div>

        {/* Premium Upgrade */}
        <ModernCard className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mr-4">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Upgrade to Premium</h4>
                <p className="text-sm text-gray-600">Unlock advanced features and personalized guidance</p>
              </div>
            </div>
            <button 
              onClick={() => navigate('/subscription')}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white py-2 px-4 rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
            >
              Upgrade
            </button>
          </div>
        </ModernCard>

        {/* Quick Stats */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Progress</h3>
          <div className="grid grid-cols-2 gap-4">
            <ModernCard className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600 mb-1">18</div>
              <div className="text-sm text-gray-600">Lessons Completed</div>
            </ModernCard>
            <ModernCard className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600 mb-1">7</div>
              <div className="text-sm text-gray-600">Day Streak</div>
            </ModernCard>
            <ModernCard className="p-4 text-center">
              <div className="text-2xl font-bold text-purple-600 mb-1">1,250</div>
              <div className="text-sm text-gray-600">Points Earned</div>
            </ModernCard>
            <ModernCard className="p-4 text-center">
              <div className="text-2xl font-bold text-orange-600 mb-1">8</div>
              <div className="text-sm text-gray-600">Achievements</div>
            </ModernCard>
          </div>
        </div>

        {/* Sign Out */}
        <ModernCard className="p-4">
          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-center text-red-600 hover:text-red-700 transition-colors"
          >
            <LogOut className="w-5 h-5 mr-2" />
            <span className="font-medium">Sign Out</span>
          </button>
        </ModernCard>

        {/* App Info */}
        <div className="text-center py-4">
          <div className="flex items-center justify-center mb-2">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-2">
              <span className="text-white font-bold text-sm">MS</span>
            </div>
            <span className="font-semibold text-gray-900">MyNewStart</span>
          </div>
          <p className="text-sm text-gray-500">Version 1.0.0</p>
          <p className="text-xs text-gray-400 mt-1" data-testid="site-copyright">{SITE_COPYRIGHT}</p>
        </div>
      </div>
    </div>
  );
};

export default MorePage;
